import { useState, useEffect, useCallback } from 'react';
import { Search, Loader2, AlertCircle, X, Package, Trash2, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PartSaleTracker = () => {
  const perms = detectPerms();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [filterProduct, setFilterProduct] = useState('');
  const [filterProductInput, setFilterProductInput] = useState('');
  const [showProductSuggest, setShowProductSuggest] = useState(false);
  const [viewTracker, setViewTracker] = useState(null);
  const [consumption, setConsumption] = useState([]);
  const [consumptionLoading, setConsumptionLoading] = useState(false);

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/part-sale-tracker`, getAuthHeaders());
      setItems(res.data);
    } catch { setError('Failed to fetch data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/part-sale-tracker/${id}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchItems();
    } catch { setError('Failed to delete'); }
  };

  const openConsumption = async (tracker) => {
    setViewTracker(tracker);
    setConsumptionLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/part-sale-tracker/${tracker.id}/consumption`, getAuthHeaders());
      setConsumption(res.data.consumption || []);
    } catch { setConsumption([]); }
    finally { setConsumptionLoading(false); }
  };

  const normalize = (str) => (str || '').toLowerCase().replace(/[\s\-\/\\.,:;_()+]+/g, '');
  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const productNames = [...new Set(items.map(i => i.product_name).filter(Boolean))].sort();
  const productSuggestions = filterProductInput.trim()
    ? productNames.filter(n => n.toLowerCase().includes(filterProductInput.toLowerCase()))
    : productNames.slice(0, 8);

  const filtered = items.filter(i => {
    const terms = searchQuery.trim().split(/\s+/).filter(Boolean);
    const matchSearch = !terms.length || (() => {
      const haystack = [i.product_name, i.product_code, i.item_id].map(normalize).join('');
      const pattern = new RegExp(terms.map(t => `(?=.*${escapeRegex(normalize(t))})`).join(''), 'i');
      return pattern.test(haystack);
    })();
    const matchProduct = !filterProduct || i.product_name === filterProduct;
    return matchSearch && matchProduct;
  });

  const getProgressColor = (remaining, full) => {
    const pct = full > 0 ? (remaining / full) * 100 : 0;
    if (pct > 60) return 'bg-green-500';
    if (pct > 25) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div data-testid="part-sale-tracker-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Part Sale Tracker</h1>
          <p className="text-slate-600 mt-1">Track products sold in parts</p>
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input type="text" placeholder="Search by product name, code or item ID..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div className="relative min-w-[200px]">
            <input
              type="text"
              value={filterProductInput}
              onChange={e => { setFilterProductInput(e.target.value); setFilterProduct(''); setShowProductSuggest(true); }}
              onFocus={() => setShowProductSuggest(true)}
              onBlur={() => setTimeout(() => setShowProductSuggest(false), 150)}
              placeholder="Filter by product..."
              data-testid="filter-product"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
            />
            {filterProductInput && (
              <button onClick={() => { setFilterProduct(''); setFilterProductInput(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            )}
            {showProductSuggest && productSuggestions.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-0.5 bg-white border border-slate-200 rounded-sm shadow-lg max-h-48 overflow-auto">
                {productSuggestions.map(name => (
                  <button key={name} onMouseDown={() => { setFilterProduct(name); setFilterProductInput(name); setShowProductSuggest(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 hover:text-orange-700 ${filterProduct === name ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-700'}`}>
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {filterProduct && (
            <button onClick={() => { setFilterProduct(''); setFilterProductInput(''); }} data-testid="clear-filters-btn"
              className="text-xs text-red-500 hover:text-red-600 font-medium px-2">Clear</button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Package className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No part sale products</h3>
            <p className="text-slate-500">Mark products as "Part Sale" in the Products page to track them here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Code</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Item ID</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Unit</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Full Qty</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Sold</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Remaining</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider min-w-[200px]">Usage</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const pct = item.full_quantity > 0 ? ((item.full_quantity - item.remaining_quantity) / item.full_quantity) * 100 : 0;
                  return (
                    <tr key={item.id} data-testid={`tracker-row-${item.id}`}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-900">{item.product_name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-slate-600 font-mono">{item.product_code || '-'}</td>
                      <td className="px-6 py-4 text-sm font-mono text-slate-700">{item.item_id || '-'}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{item.unit_name || '-'}</td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">{item.full_quantity}</td>
                      <td className="px-6 py-4 text-sm font-medium text-red-600">{item.sold_quantity}</td>
                      <td className="px-6 py-4 text-sm font-bold text-green-700">{item.remaining_quantity}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${getProgressColor(item.remaining_quantity, item.full_quantity)}`}
                              style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-slate-500 w-10 text-right">{pct.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openConsumption(item)} data-testid={`view-btn-${item.id}`}
                            className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors" title="View Consumption">
                            <Eye className="h-4 w-4" />
                          </button>
                          {perms.canDelete && (
                          <button onClick={() => setDeleteConfirm(item.id)} data-testid={`delete-btn-${item.id}`}
                            className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Consumption Modal */}
      {viewTracker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Consumption History</h3>
                <p className="text-sm text-slate-500">{viewTracker.product_name} - {viewTracker.item_id || 'No Item ID'}</p>
              </div>
              <button onClick={() => setViewTracker(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6">
              {consumptionLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
              ) : consumption.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No consumption records found</p>
              ) : (
                <div className="space-y-3">
                  {consumption.map((c, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200">
                      <div>
                        <span className="text-sm font-medium text-slate-900">{c.amount} {viewTracker.unit_name || 'units'}</span>
                        <span className="text-sm text-slate-500 mx-2">in</span>
                        <button onClick={() => { setViewTracker(null); navigate(`/admin/sale/invoices/${c.invoice_id}`); }}
                          className="text-sm font-medium text-orange-600 hover:text-orange-700 underline" data-testid={`consumption-link-${idx}`}>
                          {c.invoice_number}
                        </button>
                      </div>
                      {c.invoice_date && <span className="text-xs text-slate-400">{new Date(c.invoice_date).toLocaleDateString('en-IN')}</span>}
                    </div>
                  ))}
                  <div className="pt-3 border-t border-slate-200 flex justify-between text-sm font-bold">
                    <span className="text-slate-700">Total Consumed</span>
                    <span className="text-red-600">{consumption.reduce((s, c) => s + c.amount, 0)} {viewTracker.unit_name || 'units'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Remove this product from part sale tracking?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
